import React from "react";
import { Quote } from "lucide-react";

const members = [
  { name: "Otto Junior", role: "Guitar & Vocals" },
  { name: "Theis Jakob", role: "Bass" },
  { name: "Ola Grindbakken", role: "Drums" },
  { name: "Leo Haugen Wyller", role: "Organ" },
];

export default function Bio() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative pt-14 md:pt-20 pb-6 md:pb-8 px-6 md:px-12 bg-vintage-bg2 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto">
        {/* Intro */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5 relative">
            <div className="absolute -top-4 -left-4 right-4 bottom-4 border border-vintage-rust/60 z-0" />
            <img
              src="/band-pink.jpg"
              alt="Otto Junior live in Asia"
              className="relative w-full aspect-[4/5] object-cover z-10"
              style={{ filter: "contrast(1.05) brightness(0.95) saturate(1.0)" }}
            />
            <span className="absolute -bottom-3 left-6 z-20 bg-vintage-rust text-vintage-cream font-mono text-[10px] tracking-[0.3em] uppercase px-3 py-1">
              Live at Notodden Blues Festival 2023
            </span>
          </div>

          <div className="md:col-span-7">
            <p className="font-mono text-xs tracking-[0.35em] uppercase text-vintage-rust mb-4">
              / / The Story So Far
            </p>
            <h2 className="font-heading text-5xl md:text-7xl uppercase text-vintage-gold tracking-wide leading-none mb-5">
              About <br /> Otto Junior
            </h2>

            <p className="font-body text-lg text-vintage-cream/85 leading-relaxed mb-5">
              <span className="text-vintage-rust font-medium">Otto Junior</span> is
              an award-winning, 20-year-old blues guitarist and songwriter.
              With extensive touring across Norway, Germany, Sweden, Denmark,
              and Croatia, the <em className="font-subheading">Otto Junior Band</em> has
              proven itself as a must-see live act.
            </p>

            <p className="font-body text-lg text-vintage-cream/85 leading-relaxed mb-5">
              In October and November 2025, Otto and his band wrapped a major
              tour with a total of <span className="text-vintage-gold">19 gigs in Japan and South Korea</span>.
              He has shared stages with international blues legends including
              Christone "Kingfish" Ingram, Little Steven, Josh Smith, Kirk
              Fletcher, Matt Schofield, Mike Zito, Kid Andersen, and D.K. Harrell.
            </p>

            <p className="font-body text-lg text-vintage-cream/85 leading-relaxed">
              A proud member of the <span className="text-vintage-gold">Gibson</span> family,
              Otto opened for Slash, Billy Gibbons, and Don Felder at NAMM 2020
              in Anaheim. In 2025, the Norwegian Blues Union named him
              <em className="font-subheading text-vintage-cream"> "Most Up &amp; Coming Artist"</em> —
              cementing his place as one of Norway's most exciting young blues
              artists.
            </p>

          </div>
        </div>

        {/* The Band block */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 md:order-1 order-2">
            <div className="mb-5">
              <p className="font-subheading italic text-vintage-rust/90 text-3xl md:text-4xl leading-none mb-2">
                The
              </p>
              <h3 className="font-heading text-6xl md:text-8xl uppercase text-vintage-gold tracking-tight leading-[0.85]">
                Otto Junior
              </h3>
              <div className="flex items-center gap-4 mt-4">
                <span className="h-px flex-1 bg-vintage-rust/50" />
                <span className="font-heading text-2xl md:text-3xl tracking-[0.5em] uppercase text-vintage-cream">
                  Band
                </span>
                <span className="h-px flex-1 bg-vintage-rust/50" />
              </div>
            </div>

            <p className="font-body text-lg text-vintage-cream/85 leading-relaxed mb-6">
              Backed by <span className="text-vintage-cream">Theis Jakob</span> on bass,
              <span className="text-vintage-cream"> Ola Grindbakken</span> on drums, and
              <span className="text-vintage-cream"> Leo Haugen Wyller</span> on organ,
              the band delivers a dynamic and electrifying live show — blending
              blues, soul, blues-rock and funk. Their sets feature powerful
              interpretations of Gary Moore and Jimi Hendrix classics alongside
              strong original material.
            </p>

            <p className="font-body text-lg text-vintage-cream/85 leading-relaxed">
              All in their early twenties — but playing blues like the good
              old men. After years on the road together they did their first
              ever US gig at the legendary
              <span className="text-vintage-rust"> Springing The Blues Festival </span>
              in Jacksonville, Florida, April 2026.
            </p>

            <ul className="mt-10 grid grid-cols-2 gap-3">
              {members.map((m) => (
                <li
                  key={m.name}
                  className="border-l-2 border-vintage-rust/70 pl-4 py-1"
                  data-testid={`member-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <p className="font-subheading text-xl text-vintage-cream leading-tight">
                    {m.name}
                  </p>
                  <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-vintage-gold mt-1">
                    {m.role}
                  </p>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-5 md:order-2 order-1 relative">
            <div className="absolute -top-4 -right-4 left-4 bottom-4 border border-vintage-gold/60 z-0" />
            <img
              src="https://customer-assets.emergentagent.com/job_sonic-hub-81/artifacts/1yrdhu1b_IMG_2052%205.jpg"
              alt="Otto Junior Band on stage"
              className="relative w-full aspect-[4/5] object-cover z-10"
              style={{ filter: "sepia(0.18) contrast(1.05) brightness(0.95) saturate(0.95)" }}
            />
            <span className="absolute -bottom-3 right-6 z-20 bg-vintage-gold text-vintage-bg font-mono text-[10px] tracking-[0.3em] uppercase px-3 py-1">
              Live from South Korea · 2025
            </span>
          </div>
        </div>

        {/* Pull quote */}
        <figure className="mt-12 max-w-3xl mx-auto text-center">
          <Quote className="text-vintage-rust mb-3 mx-auto" size={22} />
          <blockquote className="font-subheading italic text-2xl md:text-3xl text-vintage-cream leading-snug">
            "One of Norway's most exciting young blues artists."
          </blockquote>
          <figcaption className="mt-5 flex items-center justify-center gap-4">
            <span className="h-px w-12 bg-vintage-gold/50" />
            <span className="font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase text-vintage-gold">
              Norwegian Blues Union · 2025
            </span>
            <span className="h-px w-12 bg-vintage-gold/50" />
          </figcaption>
        </figure>
      </div>
    </section>
  );
}
